import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Button,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Snackbar,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useRecoilValue } from "recoil";
import axios from "axios";

import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";

// =========================================
// 型定義
// =========================================

// 一覧表示用
type SalesHistoryRow = {
  company_id: number;
  company_name: string;
  contact_person: string;
  sales_person: string;
  sales_date: string;
  sales_approach_method: string;
  purchase_intention: string;
  proposed_service: string;
  proposal_details: string;
  man_hours: string;
  sales_review: string;
  next_action_id: number;
  sales_status_id: number;
  sales_status: string;
  lost_reason_id?: number | null;
  lost_reason_name?: string | null;
  recycle_base_priority?: string | null;
};

// フォーム用
type SalesHistoryForm = {
  company_id: number | "";
  company_name: string;
  contact_person: string;
  sales_person_id: number | "";
  sales_date: string;
  sales_approach_method_id: number | "";
  purchase_intention_id: number | "";
  proposed_service_id: number | "";
  proposal_details: string;
  man_hours: string;
  sales_review: string;
  next_action_id: number | "";
  sales_status_id: number | "";
  lost_reason_id: number | "";
};

// マスタ共通
type MasterItem = {
  id: number;
  name: string;
  recycle_base_priority?: string;
};

type Masters = {
  company: MasterItem[];
  sales_person: MasterItem[];
  sales_approach_method: MasterItem[];
  purchase_intention: MasterItem[];
  proposed_service: MasterItem[];
  next_action: MasterItem[];
  sales_status: MasterItem[];
  lost_reason: MasterItem[];
};

type SnackbarSeverity = "success" | "error" | "warning" | "info";

type SnackbarState = {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
};

// =========================================
// 初期値
// =========================================

const emptyFormData: SalesHistoryForm = {
  company_id: "",
  company_name: "",
  contact_person: "",
  sales_person_id: "",
  sales_date: "",
  sales_approach_method_id: "",
  purchase_intention_id: "",
  proposed_service_id: "",
  proposal_details: "",
  man_hours: "",
  sales_review: "",
  next_action_id: "",
  sales_status_id: "",
  lost_reason_id: "",
};

const emptyMasters: Masters = {
  company: [],
  sales_person: [],
  sales_approach_method: [],
  purchase_intention: [],
  proposed_service: [],
  next_action: [],
  sales_status: [],
  lost_reason: [],
};

// =========================================
// マスタ整形
// PostgreSQLから返る id が文字列になる場合があるため数値化
// =========================================

const normalizeMasterList = (
  list: any[] | undefined,
  idKeys: string[] = ["id"],
  nameKeys: string[] = ["name"],
): MasterItem[] => {
  return (list ?? []).map((item) => {
    const idValue = idKeys
      .map((key) => item?.[key])
      .find((value) => value != null);
    const nameValue = nameKeys
      .map((key) => item?.[key])
      .find((value) => value != null);

    return {
      id: Number(idValue ?? 0),
      name: String(nameValue ?? ""),
      recycle_base_priority:
        item?.recycle_base_priority ?? item?.Recycle_base_priority ?? "",
    };
  });
};

const normalizeMasters = (source: any): Masters => {
  return {
    company: normalizeMasterList(
      source?.company,
      ["id", "company_id"],
      ["name", "company_name"],
    ),
    sales_person: normalizeMasterList(
      source?.sales_person,
      ["id", "sales_person_id"],
      ["name", "sales_person"],
    ),
    sales_approach_method: normalizeMasterList(
      source?.sales_approach_method,
      ["id", "sales_approach_method_id"],
      ["name", "sales_approach_method"],
    ),
    purchase_intention: normalizeMasterList(
      source?.purchase_intention,
      ["id", "purchase_intention_id"],
      ["name", "purchase_intention"],
    ),
    proposed_service: normalizeMasterList(
      source?.proposed_service,
      ["id", "proposed_service_id"],
      ["name", "proposed_service"],
    ),
    next_action: normalizeMasterList(
      source?.next_action,
      ["id", "next_action_id"],
      ["name", "next_action"],
    ),
    sales_status: normalizeMasterList(
      source?.sales_status,
      ["id", "sales_status_id"],
      ["name", "sales_status"],
    ),
    lost_reason: normalizeMasterList(
      source?.lost_reason ??
        source?.lost_reason_list ??
        source?.master_lost_reason,
      ["id", "lost_reason_id"],
      ["name", "lost_reason_name"],
    ),
  };
};

const LOST_SALES_STATUS_ID = 7;

const isLostStatusItem = (item: MasterItem | undefined) => {
  if (!item) return false;

  const name = item.name.normalize("NFKC");

  return (
    item.id === LOST_SALES_STATUS_ID ||
    name.includes("失注") ||
    name.includes("見送り") ||
    name.includes("停止")
  );
};

// =========================================
// ラジオボタン共通部品
// 選択肢が少なく、増減が少ない項目向け
// =========================================

type RadioMasterFieldProps = {
  label: string;
  value: number | "";
  options: MasterItem[];
  onChange: (value: number) => void;
  accentColor?: string;
  required?: boolean;
  helperText?: string;
};

const RadioMasterField = ({
  label,
  value,
  options,
  onChange,
  accentColor = "#C5E763",
  required = false,
  helperText,
}: RadioMasterFieldProps) => {
  return (
    <FormControl
      fullWidth
      required={required}
      sx={{
        borderRadius: 2,
        backgroundColor: "#2D3446",
        border: "1px solid rgba(255,255,255,0.35)",
        px: 1.5,
        py: 1.25,
        transition: "border-color .18s ease, box-shadow .18s ease",
        "&:hover": {
          borderColor: "rgba(255,255,255,0.55)",
        },
        "&:focus-within": {
          borderColor: accentColor,
          boxShadow: `0 0 0 2px ${accentColor}22`,
        },
      }}
    >
      <FormLabel
        sx={{
          color: "#AEB7C5",
          fontSize: "0.86rem",
          fontWeight: 700,
          mb: 1,
          "&.Mui-focused": {
            color: accentColor,
          },
          "& .MuiFormLabel-asterisk": {
            color: "#ff8579",
          },
        }}
      >
        {label}
      </FormLabel>

      <RadioGroup
        value={value === "" ? "" : String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fit, minmax(130px, 1fr))",
          },
          gap: 1,
        }}
      >
        {options.map((item) => {
          const checked = value !== "" && Number(value) === item.id;

          return (
            <FormControlLabel
              key={item.id}
              value={String(item.id)}
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "rgba(229,231,235,0.62)",
                    p: 0.75,
                    "&.Mui-checked": {
                      color: accentColor,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: checked ? "#F9FAFB" : "#E5E7EB",
                    fontSize: "0.9rem",
                    fontWeight: checked ? 800 : 600,
                    lineHeight: 1.25,
                  }}
                >
                  {item.name}
                </Typography>
              }
              sx={{
                m: 0,
                px: 1,
                py: 0.8,
                borderRadius: 2,
                border: checked
                  ? `1px solid ${accentColor}`
                  : "1px solid rgba(255,255,255,0.10)",
                backgroundColor: checked
                  ? `${accentColor}1F`
                  : "rgba(255,255,255,0.035)",
                transition:
                  "border-color .18s ease, background-color .18s ease, transform .18s ease",
                "&:hover": {
                  borderColor: accentColor,
                  backgroundColor: `${accentColor}14`,
                  transform: "translateY(-1px)",
                },
              }}
            />
          );
        })}
      </RadioGroup>

      {helperText && (
        <FormHelperText
          sx={{
            color: "#AEB7C5",
            mx: 0,
            mt: 0.9,
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// =========================================
// コンポーネント
// =========================================

const SalesReport = () => {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [listData, setListData] = useState<SalesHistoryRow[]>([]);
  const [masters, setMasters] = useState<Masters>(emptyMasters);
  const [formData, setFormData] = useState<SalesHistoryForm>(emptyFormData);

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: SnackbarSeverity = "success",
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const selectedSalesStatus = masters.sales_status.find(
    (item) => item.id === formData.sales_status_id,
  );
  const isLostStatusSelected = isLostStatusItem(selectedSalesStatus);

  const selectedLostReason = masters.lost_reason.find(
    (item) => item.id === formData.lost_reason_id,
  );
  const recyclePriority = selectedLostReason?.recycle_base_priority ?? "";

  // =========================================
  // 一覧 + マスタ再取得
  // =========================================

  const refreshList = async () => {
    const refreshRes = await axios.post(
      ConstList.getApiUrl() + "ProspectiveSalesReport.php",
      {
        flag: "Pros_Sales_Report",
        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
      },
      ConstList.AXIOS_CONFIG,
    );

    const data = refreshRes.data?.data;

    if (data?.list) {
      setListData(data.list);
      setMasters(normalizeMasters(data.masters));
    } else {
      setListData([]);
      setMasters(emptyMasters);
    }
  };

  // =========================================
  // 初期データ取得
  // =========================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await axios.post(
          ConstList.getApiUrl() + "ProspectiveSalesReport.php",
          {
            flag: "Pros_Sales_Report",
            access_token: loginUserData.accessToken,
            algorithm: loginUserData.algorithm,
          },
          ConstList.AXIOS_CONFIG,
        );

        console.log("API raw response:", res.data);

        if (!res.data || res.data.data === false || !res.data.data?.list) {
          console.warn("list データが存在しません", res.data);
          setListData([]);
          setMasters(emptyMasters);
          return;
        }

        const data = res.data?.data;

        console.log("list:", data.list);
        console.log("masters:", data.masters);

        setListData(data.list);
        setMasters(normalizeMasters(data.masters));
      } catch (err) {
        console.error("取得エラー", err);
        setErrorMessage("データ取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    loginUserData.accessToken,
    loginUserData.algorithm,
  ]);

  // =========================================
  // 保存
  // =========================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingItems: string[] = [];

    if (!formData.company_id || !formData.company_name.trim()) {
      missingItems.push("会社名");
    }

    if (!formData.contact_person.trim()) {
      missingItems.push("顧客窓口");
    }

    if (!formData.sales_person_id) {
      missingItems.push("営業担当者");
    }

    if (!formData.sales_date) {
      missingItems.push("アプローチ日");
    }

    if (!formData.sales_approach_method_id) {
      missingItems.push("営業方法");
    }

    if (!formData.purchase_intention_id) {
      missingItems.push("購買意欲");
    }

    if (!formData.proposed_service_id) {
      missingItems.push("提案サービス");
    }

    if (!formData.proposal_details.trim()) {
      missingItems.push("提案内容");
    }

    if (!formData.sales_status_id) {
      missingItems.push("営業後の進捗状況");
    }

    if (isLostStatusSelected && !formData.lost_reason_id) {
      missingItems.push("失注・停止理由");
    }

    if (missingItems.length > 0) {
      showSnackbar(`${missingItems.join("、")}を入力してください`, "warning");
      return;
    }

    if (formData.man_hours !== "" && !/^[0-9]+$/.test(formData.man_hours)) {
      showSnackbar("対応にかかった時間は数字のみ入力してください", "warning");
      return;
    }

    try {
      setIsSaving(true);

      const res = await axios.post(
        ConstList.getApiUrl() + "ProspectiveSalesReport.php",
        {
          flag: "Insert_Pros_Sales_Report",
          ...formData,

          // 登録時に不要な空白を取り除く
          company_name: formData.company_name.trim(),
          contact_person: formData.contact_person.trim(),
          proposal_details: formData.proposal_details.trim(),
          sales_review: formData.sales_review.trim(),
          lost_reason_id: isLostStatusSelected ? formData.lost_reason_id : null,

          // 登録時だけ数値に変換
          man_hours: formData.man_hours === "" ? 0 : Number(formData.man_hours),

          access_token: loginUserData.accessToken,
          algorithm: loginUserData.algorithm,
        },
        ConstList.AXIOS_CONFIG,
      );

      console.log("登録結果:", res.data);

      if (res.data?.data === false) {
        showSnackbar(res.data?.message ?? "登録に失敗しました", "error");
        return;
      }

      await refreshList();

      // 成功した時だけフォームを初期化
      setFormData(emptyFormData);

      showSnackbar("登録完了！未受注案件管理画面で確認できます！", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("登録に失敗しました", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // =========================================
  // キャンセル
  // =========================================

  const handleCancel = () => {
    if (!window.confirm("入力内容を破棄しますか？")) return;
    setFormData(emptyFormData);
  };

  // =========================================
  // 入力欄デザイン
  // =========================================

  const writableInputPropsSx = {
    backgroundColor: "#E6EAF2",
    color: "#111827",

    "& .MuiOutlinedInput-input": {
      color: "#111827",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(45,52,70,0.5)",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(45,52,70,0.75)",
    },

    "&.Mui-focused": {
      backgroundColor: "#E6EAF2",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2D3446",
    },
  };

  const writableLabelSx = {
    "& .MuiInputLabel-root": {
      color: "#8E8E8E",
      fontSize: "0.9rem",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#111827",
      fontSize: "1.05rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },

    "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": {
      color: "#a7aebd",
    },

    "& .MuiSelect-icon": {
      color: "#C5E763",
    },
  };

  const darkFixedInputSx = {
    backgroundColor: "#2D3446",
    color: "#E5E7EB",

    "& .MuiOutlinedInput-input": {
      color: "#E5E7EB",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.35)",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.55)",
    },

    "&.Mui-focused": {
      backgroundColor: "#2D3446",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.75)",
    },

    "&.Mui-focused .MuiOutlinedInput-input": {
      color: "#E5E7EB",
    },

    "& .MuiSelect-icon": {
      color: "#C5E763",
    },

    "& .MuiAutocomplete-popupIndicator": {
      color: "#C5E763",
    },

    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter:
        "invert(85%) sepia(45%) saturate(600%) hue-rotate(20deg) brightness(100%) contrast(95%)",
      opacity: 0.95,
      cursor: "pointer",
    },
  };

  const reflectionInputPropsSx = {
    backgroundColor: "#E6EAF2",
    color: "#111827",

    "& .MuiOutlinedInput-input": {
      color: "#111827",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(45,52,70,0.4)",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(45,52,70,0.55)",
    },

    "&.Mui-focused": {
      backgroundColor: "#E6EAF2",
      boxShadow: "0 0 0 1px rgba(45,52,70,0.18)",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(45,52,70,0.55)",
      borderWidth: 1,
    },
  };

  // =========================================
  // 画面全体の共通UI
  // =========================================

  const reportPageSx = {
    backgroundColor: "#2D2C3A",
    borderRadius: 3,
    px: { xs: 2, sm: 3 },
  };

  const reportCardSx = {
    mb: 3,
    backgroundColor: "#2D3446",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 3,
    boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
  };

  const reportAccentCardSx = {
    ...reportCardSx,
    backgroundColor: "#263044",
    border: "1px solid rgba(197,231,99,0.35)",
  };

  const reportMemoCardSx = {
    ...reportCardSx,
    backgroundColor: "#F8FAFC",
    border: "1px solid rgba(156,163,175,0.75)",
  };

  const cardContentSx = {
    p: { xs: 2, sm: 3 },
  };

  const SectionHeader = ({
    title,
    description,
    accent = false,
    darkText = false,
  }: {
    title: string;
    description?: string;
    accent?: boolean;
    darkText?: boolean;
  }) => (
    <Box mb={2.5}>
      <Typography
        fontWeight={700}
        sx={{
          color: darkText ? "#111827" : accent ? "#C5E763" : "#F3F4F6",
          fontSize: "1rem",
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          sx={{
            color: darkText ? "#4B5563" : "#AEB7C5",
            mt: 0.5,
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );

  // 営業日報登録画面では選ばせない進捗状況
  // 1 = 未アプローチ / 未着手 の想定
  const selectableSalesStatusList = masters.sales_status.filter(
    (item) =>
      item.id !== 1 && item.name !== "未アプローチ" && item.name !== "未着手",
  );

  return (
    <>
      <form onSubmit={handleSubmit} noValidate>
        <Box maxWidth={900} mx="auto" py={4} sx={reportPageSx}>
          {/* ===== タイトル ===== */}
          <Box mb={3}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color: "#F9FAFB",
                letterSpacing: "0.04em",
              }}
            >
              営業日報
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#AEB7C5",
                mt: 0.75,
              }}
            >
              営業活動の内容・結果・次回対応を記録します。
            </Typography>
          </Box>

          {errorMessage && (
            <Box mb={2}>
              <Typography color="error">{errorMessage}</Typography>
            </Box>
          )}

          {/* ===== 基本情報 ===== */}
          <Card sx={reportCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title="基本情報"
                description="訪問先・担当者・営業日など、営業活動の基本情報を入力します。"
              />

              <Stack spacing={2.5}>
                <Autocomplete
                  fullWidth
                  options={masters.company}
                  getOptionLabel={(option) => option.name ?? ""}
                  value={
                    masters.company.find(
                      (row) => row.id === formData.company_id,
                    ) ?? null
                  }
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      company_id: newValue?.id ?? "",
                      company_name: newValue?.name ?? "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="会社名"
                      required
                      variant="outlined"
                      size="small"
                      sx={darkFixedInputSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#E5E7EB" }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="営業担当者"
                    required
                    value={formData.sales_person_id}
                    sx={darkFixedInputSx}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sales_person_id: Number(e.target.value),
                      })
                    }
                  >
                    {masters.sales_person.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    type="date"
                    label="アプローチ日"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={formData.sales_date}
                    sx={darkFixedInputSx}
                    onChange={(e) =>
                      setFormData({ ...formData, sales_date: e.target.value })
                    }
                  />
                </Stack>

                <TextField
                  fullWidth
                  label="顧客窓口"
                  placeholder="例：〇〇部 △△様"
                  required
                  value={formData.contact_person}
                  sx={darkFixedInputSx}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contact_person: e.target.value,
                    })
                  }
                />
              </Stack>
            </CardContent>
          </Card>

          {/* ===== 商談内容 ===== */}
          <Card sx={reportAccentCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title="商談内容"
                description="営業方法、購買意欲、提案サービス、提案内容を記録します。"
                accent
              />

              <Stack spacing={2.5}>
                <TextField
                  select
                  fullWidth
                  label="営業方法"
                  required
                  value={formData.sales_approach_method_id}
                  sx={darkFixedInputSx}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sales_approach_method_id: Number(e.target.value),
                    })
                  }
                >
                  {masters.sales_approach_method.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="対応にかかった時間（min）"
                  value={formData.man_hours}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "" || /^[0-9]+$/.test(value)) {
                      setFormData({
                        ...formData,
                        man_hours: value,
                      });
                    }
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  InputProps={{ sx: writableInputPropsSx }}
                  sx={writableLabelSx}
                  fullWidth
                />

                <RadioMasterField
                  label="購買意欲"
                  required
                  value={formData.purchase_intention_id}
                  options={masters.purchase_intention}
                  accentColor="#C5E763"
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      purchase_intention_id: value,
                    })
                  }
                />

                <RadioMasterField
                  label="提案サービス"
                  required
                  value={formData.proposed_service_id}
                  options={masters.proposed_service}
                  accentColor="#64B5F6"
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      proposed_service_id: value,
                    })
                  }
                />

                <TextField
                  label="提案内容"
                  multiline
                  rows={4}
                  required
                  value={formData.proposal_details}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      proposal_details: e.target.value,
                    })
                  }
                  InputProps={{ sx: writableInputPropsSx }}
                  sx={writableLabelSx}
                  placeholder="例：提案した内容、相手の反応、課題として挙がった内容など"
                  FormHelperTextProps={{
                    sx: {
                      color: "#AEB7C5",
                      ml: 0,
                    },
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* ===== 結果・次回対応 ===== */}
          <Card sx={reportCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title="結果・次回対応"
                description="この営業活動後の顧客状態と、次に行う対応を記録します。"
              />

              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="営業後の進捗状況"
                    required
                    value={formData.sales_status_id}
                    sx={darkFixedInputSx}
                    onChange={(e) => {
                      const statusId = Number(e.target.value);
                      const statusItem = masters.sales_status.find(
                        (item) => item.id === statusId,
                      );

                      setFormData({
                        ...formData,
                        sales_status_id: statusId,
                        lost_reason_id: isLostStatusItem(statusItem)
                          ? formData.lost_reason_id
                          : "",
                      });
                    }}
                    helperText="顧客一覧のアプローチ状況に反映されます。"
                    FormHelperTextProps={{
                      sx: {
                        color: "#AEB7C5",
                        mt: 0.75,
                      },
                    }}
                  >
                    {selectableSalesStatusList.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="次回対応"
                    value={formData.next_action_id}
                    sx={darkFixedInputSx}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        next_action_id: Number(e.target.value),
                      })
                    }
                  >
                    {masters.next_action.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                {isLostStatusSelected && (
                  <Box
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,133,121,0.10)",
                      border: "1px solid rgba(255,133,121,0.35)",
                    }}
                  >
                    <TextField
                      select
                      fullWidth
                      required
                      label="失注・停止理由"
                      value={formData.lost_reason_id}
                      sx={darkFixedInputSx}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          lost_reason_id: Number(e.target.value),
                        })
                      }
                      helperText={
                        masters.lost_reason.length === 0
                          ? "失注・停止理由マスターが取得できていません。"
                          : "失注・停止扱いの営業履歴として、一覧では行背景の判定に使用されます。"
                      }
                      FormHelperTextProps={{
                        sx: {
                          color: "#AEB7C5",
                          mt: 0.75,
                        },
                      }}
                    >
                      {masters.lost_reason.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {recyclePriority && (
                      <Typography
                        sx={{
                          mt: 1,
                          color: "#F9FAFB",
                          fontSize: 12.5,
                          fontWeight: 700,
                        }}
                      >
                        再アプローチ優先度：{recyclePriority}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ===== 振り返りメモ ===== */}
          <Card sx={reportMemoCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title="振り返りメモ"
                description="次回の営業や社内共有に活かしたい内容を自由に記録します。"
                darkText
              />

              <TextField
                label="振り返りメモ"
                multiline
                rows={3}
                fullWidth
                value={formData.sales_review}
                onChange={(e) =>
                  setFormData({ ...formData, sales_review: e.target.value })
                }
                InputProps={{ sx: reflectionInputPropsSx }}
                sx={writableLabelSx}
                placeholder="例：相手の温度感、次回確認すること、提案時の反省点など"
              />
            </CardContent>
          </Card>

          {/* ===== 操作 ===== */}
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            sx={{
              position: "sticky",
              bottom: 0,
              py: 2,
              background:
                "linear-gradient(180deg, rgba(45,44,58,0.65), rgba(45,44,58,1))",
              zIndex: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                borderColor: "rgba(255,255,255,0.35)",
                color: "#E5E7EB",
                borderRadius: 2,
                px: 3,
                "&:hover": {
                  borderColor: "#C5E763",
                  backgroundColor: "rgba(197,231,99,0.08)",
                },
              }}
            >
              キャンセル
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || loading}
              sx={{
                backgroundColor: "#C5E763",
                color: "#111827",
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                "&:hover": {
                  backgroundColor: "#D7F580",
                },
              }}
            >
              {isSaving ? (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              ) : (
                "記録"
              )}
            </Button>
          </Box>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;

          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }));
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SalesReport;
